/**
 * The always-on control hint strip along the bottom of the screen.
 *
 * A flight simulator has more bindings than anyone will remember, and the ones a new pilot
 * most needs — the mouse yoke above all, since nothing on screen otherwise suggests it exists
 * — are exactly the ones they will never find. So the important bindings sit in front of them
 * permanently, and `/` hides the strip once they are no longer needed.
 *
 * The mouse-yoke chip is *live*: it lights up while the yoke is engaged, which is the only
 * feedback distinguishing "the aeroplane is not responding to my mouse" from "the mouse is
 * flying the aeroplane and I did not realise".
 *
 * Same cost discipline as the other overlays: the DOM is built once and only a class name
 * changes afterwards, so a hidden strip costs one boolean per frame and a visible one costs a
 * class comparison.
 *
 * `updateFrame(alpha)`: called from the loop's after-frame slot; does nothing unless a chip's
 * state actually changed.
 */

interface Chip {
  element: HTMLSpanElement;
  active: boolean;
}

export interface HelpFooterEntry {
  /** Key cap text, e.g. `Y`. */
  keys: string;
  /** What it does. */
  label: string;
  /** Start a new group, separated by a divider. */
  startsGroup?: boolean;
  /** Give this chip an id so its active state can be driven at runtime. */
  id?: string;
}

/**
 * The bindings worth permanent screen space: everything needed to take off, fly a circuit and
 * land, plus the two view controls and the way back to the rest of them. The full table lives
 * in the README; this is the subset a pilot needs *while flying*.
 */
export const DEFAULT_HELP_ENTRIES: readonly HelpFooterEntry[] = [
  { keys: 'W/S', label: 'pitch' },
  { keys: 'A/D', label: 'roll' },
  { keys: 'Q/E', label: 'rudder' },
  { keys: '−/=', label: 'throttle' },
  { keys: ',/.', label: 'trim' },
  { keys: 'Z/X', label: 'rudder trim' },
  { keys: ';/\'', label: 'aileron trim' },
  { keys: 'F/V', label: 'flaps', startsGroup: true },
  { keys: 'B', label: 'brakes' },
  { keys: 'I', label: 'starter', id: 'starter' },
  { keys: 'J', label: 'auto-coord', id: 'coord' },
  { keys: 'L', label: 'airports', startsGroup: true },
  { keys: 'T', label: 'time & weather' },
  { keys: 'Y', label: 'mouse yoke', id: 'yoke' },
  { keys: 'C', label: 'view' },
  { keys: '6', label: 'screenshot' },
  { keys: 'P', label: 'pause' },
  { keys: 'R', label: 'respawn' },
  { keys: 'M', label: 'mute', id: 'mute' },
  { keys: 'F3', label: 'perf', startsGroup: true },
  { keys: '/', label: 'hide' },
];

export class HelpFooter {
  readonly element: HTMLDivElement;
  private readonly chips = new Map<string, Chip>();
  private visibleValue = true;

  constructor(entries: readonly HelpFooterEntry[] = DEFAULT_HELP_ENTRIES, parent: HTMLElement = document.body) {
    this.element = document.createElement('div');
    this.element.className = 'help';

    for (const entry of entries) {
      if (entry.startsGroup === true) {
        const divider = document.createElement('span');
        divider.className = 'help__divider';
        divider.setAttribute('aria-hidden', 'true');
        this.element.append(divider);
      }

      const chip = document.createElement('span');
      chip.className = 'help__chip';

      const keys = document.createElement('kbd');
      keys.className = 'help__keys';
      keys.textContent = entry.keys;

      const label = document.createElement('span');
      label.className = 'help__label';
      label.textContent = entry.label;

      chip.append(keys, label);
      this.element.append(chip);

      if (entry.id !== undefined) this.chips.set(entry.id, { element: chip, active: false });
    }

    parent.append(this.element);
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  toggle(): void {
    this.setVisible(!this.visibleValue);
  }

  setVisible(visible: boolean): void {
    this.visibleValue = visible;
    this.element.hidden = !visible;
  }

  /** Light a chip up, e.g. while the mouse yoke is flying the aircraft. */
  setActive(id: string, active: boolean): void {
    const chip = this.chips.get(id);
    if (chip === undefined || chip.active === active) return;
    chip.active = active;
    chip.element.className = active ? 'help__chip help__chip--active' : 'help__chip';
  }

  dispose(): void {
    this.element.remove();
  }
}
